import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Input,
  Select,
  Tag,
  Rate,
  Typography,
  Pagination,
  Space,
  Skeleton,
  Image,
  Carousel,
  Empty,
  message,
  Tooltip,
  Segmented,
} from 'antd';
import {
  SearchOutlined,
  ShoppingCartOutlined,
  HeartOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  FireOutlined,
  RightOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import { productService, cartService, wishlistService, storageUrl } from '../services/api';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'NPR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price || 0);
};

const CustomerProducts = () => {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');
  const [activeFilter, setActiveFilter] = useState('products');
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const pageSize = 8;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [prodRes, servRes, bannerRes] = await Promise.all([
          productService.getProducts({ limit: 50, type: 'product' }),
          productService.getProducts({ limit: 20, type: 'service' }),
          productService.getBanners(),
        ]);

        const prodData = prodRes?.data?.data || prodRes?.data || [];
        if (Array.isArray(prodData)) {
          setProducts(
            prodData.map((p) => ({
              ...p,
              image:
                storageUrl(p.images?.[0]?.url || p.images?.[0]?.image_url) ||
                `https://placehold.co/300x200/e2e8f0/64748b?text=${encodeURIComponent(p.name || 'Product')}`,
              categorySlug: (p.category || '').toLowerCase().replace(/\s+/g, '-'),
              rating: Number(p.average_rating) || p.rating || 4.5,
              reviews: p.total_reviews || Math.floor(Math.random() * 100) + 10,
              inStock:
                p.is_available !== false &&
                (p.stock === null || p.stock === undefined || p.stock !== 0),
            })),
          );
        }

        const servData = servRes?.data?.data || servRes?.data || [];
        if (Array.isArray(servData)) {
          setServices(
            servData.map((s) => ({
              ...s,
              type: s.type || 'service',
              image:
                storageUrl(s.images?.[0]?.url || s.images?.[0]?.image_url) ||
                `https://placehold.co/300x200/e2e8f0/64748b?text=${encodeURIComponent(s.name || 'Service')}`,
              categorySlug: (s.category || '').toLowerCase().replace(/\s+/g, '-'),
              rating: Number(s.average_rating) || s.rating || 4.5,
              reviews: s.total_reviews || Math.floor(Math.random() * 100) + 10,
              inStock: true,
              stock: 999,
            })),
          );
        }

        const bannerData = bannerRes?.data || [];
        if (Array.isArray(bannerData)) setBanners(bannerData.filter((b) => b.is_active !== false));
      } catch (e) {
        console.error('Failed to load data', e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleAddToCart = async (product, e) => {
    e.stopPropagation();
    try {
      await cartService.addToCart(product.id);
      message.success(`${product.name} added to cart!`);
    } catch {
      message.error('Failed to add to cart');
    }
  };

  const handleSave = async (product, e) => {
    e.stopPropagation();
    try {
      await wishlistService.addToWishlist(product.id);
      message.success(`${product.name} saved!`);
    } catch {
      message.error('Failed to save');
    }
  };

  const getCategorySlug = (name) => (name || '').toLowerCase().replace(/\s+/g, '-');

  const activeItems = activeFilter === 'products' ? products : services;
  const filtered = activeItems
    .filter((p) => selectedCategory === 'all' || getCategorySlug(p.category) === selectedCategory)
    .filter((p) => !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'price') return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      return (a.name || '').localeCompare(b.name || '');
    });

  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const categories = [...new Set(activeItems.map((p) => p.category).filter(Boolean))];

  const activeBanners = banners.filter((b) => {
    if (activeFilter === 'products')
      return ['home_top', 'new_arrivals', 'promotion'].includes(b.banner_type);
    return ['mid_screen', 'advertisement'].includes(b.banner_type);
  });

  return (
    <div>
      {/* Dynamic Banners Carousel */}
      {activeBanners.length > 0 && (
        <div style={{ marginBottom: 24, borderRadius: 16, overflow: 'hidden' }}>
          <Carousel autoplay autoplaySpeed={5000} dotPosition="bottom">
            {activeBanners.map((banner, i) => (
              <div key={banner.id || i}>
                <div
                  onClick={() => banner.target_url && window.open(banner.target_url, '_blank')}
                  style={{
                    height: 280,
                    borderRadius: 16,
                    background: `linear-gradient(135deg, ${['#1a3a2a', '#2d5a3d', '#1e4d3a', '#3d7a4d'][i % 4]}, ${['#52b788', '#3d7a4d', '#2d5a3d', '#7fb069'][i % 4]})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    cursor: banner.target_url ? 'pointer' : 'default',
                    overflow: 'hidden',
                  }}
                >
                  {banner.image_url && (
                    <img
                      src={storageUrl(banner.image_url)}
                      alt={banner.title}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: 0.3,
                      }}
                    />
                  )}
                  <div
                    style={{
                      position: 'relative',
                      zIndex: 1,
                      textAlign: 'center',
                      padding: '0 40px',
                      maxWidth: 700,
                    }}
                  >
                    <Tag
                      color={['green', 'blue', 'gold', 'green'][i % 4]}
                      style={{
                        marginBottom: 12,
                        fontSize: 13,
                        padding: '4px 16px',
                        borderRadius: 20,
                      }}
                    >
                      {banner.banner_type?.replace('_', ' ').toUpperCase() || 'FEATURED'}
                    </Tag>
                    <Title level={2} style={{ color: '#fff', margin: 0, fontSize: 32 }}>
                      {banner.title}
                    </Title>
                    {banner.description && (
                      <Paragraph
                        style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, marginTop: 8 }}
                      >
                        {banner.description}
                      </Paragraph>
                    )}
                    {banner.target_url && (
                      <Button
                        type="primary"
                        ghost
                        style={{
                          marginTop: 12,
                          borderColor: '#fff',
                          color: '#fff',
                          borderRadius: 20,
                          height: 40,
                          padding: '0 24px',
                        }}
                        onClick={() => window.open(banner.target_url, '_blank')}
                      >
                        Learn More <RightOutlined />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </Carousel>
        </div>
      )}

      {/* Header Section */}
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <Title level={3} style={{ color: '#2d5a3d', margin: 0 }}>
              {activeFilter === 'products' ? 'Products' : 'Services'}
            </Title>
            <Text style={{ color: '#6b7280' }}>
              {filtered.length} {activeFilter === 'products' ? 'products' : 'services'} available
            </Text>
          </div>
          <Space wrap>
            <Button
              icon={<FireOutlined />}
              type={activeFilter === 'products' ? 'primary' : 'default'}
              onClick={() => {
                setActiveFilter('products');
                setCurrentPage(1);
              }}
              style={
                activeFilter === 'products'
                  ? { background: '#52b788', borderColor: '#52b788', borderRadius: 20 }
                  : { borderRadius: 20 }
              }
            >
              Products
            </Button>
            <Button
              icon={<CalendarOutlined />}
              type={activeFilter === 'services' ? 'primary' : 'default'}
              onClick={() => {
                setActiveFilter('services');
                setCurrentPage(1);
              }}
              style={
                activeFilter === 'services'
                  ? { background: '#52b788', borderColor: '#52b788', borderRadius: 20 }
                  : { borderRadius: 20 }
              }
            >
              Services
            </Button>
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'grid', icon: <AppstoreOutlined /> },
                { value: 'list', icon: <UnorderedListOutlined /> },
              ]}
              style={{ borderRadius: 10 }}
            />
          </Space>
        </div>
      </div>

      {/* Filters Bar */}
      <Card
        style={{ marginBottom: 24, borderRadius: 12, border: '1px solid #e8f0e8' }}
        styles={{ body: { padding: '16px 20px' } }}
      >
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} md={8}>
            <Input
              ref={searchInputRef}
              prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
              placeholder={`Search ${activeFilter === 'products' ? 'products' : 'services'}...`}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              allowClear
              style={{ borderRadius: 10, height: 40 }}
            />
          </Col>
          <Col xs={12} md={5}>
            <Select
              value={selectedCategory}
              onChange={(v) => {
                setSelectedCategory(v);
                setCurrentPage(1);
              }}
              style={{ width: '100%' }}
              placeholder="Category"
              options={[
                { value: 'all', label: 'All Categories' },
                ...categories.map((c) => ({ value: getCategorySlug(c), label: c })),
              ]}
            />
          </Col>
          <Col xs={12} md={5}>
            <Select
              value={sortBy}
              onChange={setSortBy}
              style={{ width: '100%' }}
              options={[
                { value: 'name', label: 'Name: A-Z' },
                { value: 'price', label: 'Price: Low to High' },
                { value: 'price-desc', label: 'Price: High to Low' },
                { value: 'rating', label: 'Top Rated' },
              ]}
            />
          </Col>
          <Col xs={24} md={6}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {activeFilter === 'services' && (
                <Button
                  icon={<CalendarOutlined />}
                  onClick={() => navigate('/service-catalog')}
                  style={{ borderRadius: 10, borderColor: '#52b788', color: '#52b788' }}
                >
                  Book Service
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Card>

      {/* Products/Services Grid */}
      {loading ? (
        <Row gutter={[24, 24]}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Col xs={24} sm={12} lg={viewMode === 'grid' ? 6 : 24} key={i}>
              <Card>
                <Skeleton active paragraph={{ rows: viewMode === 'grid' ? 5 : 2 }} />
              </Card>
            </Col>
          ))}
        </Row>
      ) : paginated.length === 0 ? (
        <Card style={{ borderRadius: 12, border: '1px solid #e8f0e8' }}>
          <Empty
            description={`No ${activeFilter === 'products' ? 'products' : 'services'} found`}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
              }}
              style={{ borderRadius: 10 }}
            >
              Clear Filters
            </Button>
          </Empty>
        </Card>
      ) : (
        <Row gutter={[24, 24]}>
          {paginated.map((item) => (
            <Col xs={24} sm={12} lg={viewMode === 'grid' ? 6 : 24} key={item.id}>
              {viewMode === 'grid' ? (
                <Card
                  hoverable
                  className="customer-card"
                  cover={
                    <div
                      style={{
                        height: 200,
                        overflow: 'hidden',
                        background: '#f0faf0',
                        position: 'relative',
                      }}
                    >
                      <Image
                        src={item.image}
                        alt={item.name}
                        preview={false}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        fallback="https://placehold.co/300x200/e2e8f0/64748b?text=No+Image"
                      />
                      {item.type === 'service' && (
                        <Tag
                          color="blue"
                          style={{ position: 'absolute', top: 8, left: 8, borderRadius: 12 }}
                        >
                          Service
                        </Tag>
                      )}
                      {item.discount_value > 0 && (
                        <Tag
                          color="gold"
                          style={{ position: 'absolute', top: 8, right: 8, borderRadius: 12 }}
                        >
                          -
                          {item.discount_type === 'percentage'
                            ? `${item.discount_value}%`
                            : `NPR ${item.discount_value}`}
                        </Tag>
                      )}
                    </div>
                  }
                  onClick={() => navigate(`/customer/products/${item.id}`)}
                  style={{ borderRadius: 12, border: '1px solid #e8f0e8', overflow: 'hidden' }}
                >
                  <div style={{ marginBottom: 8 }}>
                    {item.badge && (
                      <Tag color="gold" style={{ borderRadius: 8 }}>
                        {item.badge}
                      </Tag>
                    )}
                    {item.type === 'service' ||
                    item.stock === null ||
                    item.stock === undefined ||
                    item.stock > 10 ? (
                      <Tag color="green" style={{ borderRadius: 8 }}>
                        Available
                      </Tag>
                    ) : item.stock > 0 ? (
                      <Tag color="gold" style={{ borderRadius: 8 }}>
                        Low Stock
                      </Tag>
                    ) : (
                      <Tag color="red" style={{ borderRadius: 8 }}>
                        Out of Stock
                      </Tag>
                    )}
                  </div>
                  <Text
                    strong
                    style={{
                      fontSize: 15,
                      color: '#2d5a3d',
                      display: 'block',
                      marginBottom: 4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.name}
                  </Text>
                  <Space style={{ marginBottom: 8 }}>
                    <Rate disabled defaultValue={item.rating} style={{ fontSize: 12 }} />
                    <Text style={{ fontSize: 11, color: '#9ca3af' }}>({item.reviews})</Text>
                  </Space>
                  <div
                    style={{ fontSize: 18, fontWeight: 700, color: '#2d5a3d', marginBottom: 12 }}
                  >
                    {item.final_price === 0 ? (
                      <Text style={{ color: '#52b788', fontWeight: 700, fontSize: 18 }}>Free</Text>
                    ) : (
                      formatPrice(item.final_price ?? item.price)
                    )}
                    {(item.final_price ?? item.price) < item.price && (
                      <>
                        <Text delete style={{ fontSize: 13, color: '#9ca3af', marginLeft: 8 }}>
                          {formatPrice(item.price)}
                        </Text>
                        <Tag color="red" style={{ marginLeft: 8, fontSize: 11 }}>
                          {item.final_price === 0
                            ? 'FREE for Members'
                            : `${Math.round((1 - (item.final_price ?? item.price) / item.price) * 100)}% OFF`}
                        </Tag>
                      </>
                    )}
                  </div>
                  <Space style={{ width: '100%' }}>
                    <Tooltip title="Add to Cart">
                      <Button
                        type="primary"
                        size="small"
                        icon={<ShoppingCartOutlined />}
                        onClick={(e) => handleAddToCart(item, e)}
                        style={{
                          borderRadius: 8,
                          background: '#52b788',
                          borderColor: '#52b788',
                          flex: 1,
                        }}
                        disabled={!item.inStock}
                      >
                        {item.type === 'service' ? 'Book' : 'Add to Cart'}
                      </Button>
                    </Tooltip>
                    <Tooltip title="Add to Wishlist">
                      <Button
                        size="small"
                        icon={<HeartOutlined />}
                        onClick={(e) => handleSave(item, e)}
                        style={{ borderRadius: 8, color: '#e5484d', borderColor: '#e5484d' }}
                      />
                    </Tooltip>
                  </Space>
                </Card>
              ) : (
                <Card
                  hoverable
                  onClick={() => navigate(`/customer/products/${item.id}`)}
                  style={{ borderRadius: 12, border: '1px solid #e8f0e8' }}
                  styles={{ body: { padding: 16 } }}
                >
                  <Row gutter={16} align="middle">
                    <Col xs={6} sm={4}>
                      <Image
                        src={item.image}
                        alt={item.name}
                        preview={false}
                        style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 8 }}
                        fallback="https://placehold.co/80x80/e2e8f0/64748b?text=N/A"
                      />
                    </Col>
                    <Col xs={12} sm={14}>
                      <Text strong style={{ color: '#2d5a3d', fontSize: 15, display: 'block' }}>
                        {item.name}
                      </Text>
                      {item.category && (
                        <Text style={{ color: '#9ca3af', fontSize: 12 }}>{item.category}</Text>
                      )}
                      <div style={{ marginTop: 4 }}>
                        <Rate disabled defaultValue={item.rating} style={{ fontSize: 11 }} />
                        <Text style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>
                          ({item.reviews})
                        </Text>
                      </div>
                    </Col>
                    <Col xs={6} sm={6} style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#2d5a3d' }}>
                        {item.final_price === 0
                          ? 'Free'
                          : formatPrice(item.final_price ?? item.price)}
                        {(item.final_price ?? item.price) < item.price && (
                          <>
                            <br />
                            <Text delete style={{ fontSize: 13, color: '#9ca3af' }}>
                              {formatPrice(item.price)}
                            </Text>
                            <Tag color="red" style={{ marginLeft: 4, fontSize: 11 }}>
                              {item.final_price === 0
                                ? 'FREE for Members'
                                : `${Math.round((1 - (item.final_price ?? item.price) / item.price) * 100)}% OFF`}
                            </Tag>
                          </>
                        )}
                      </div>
                      <Space style={{ marginTop: 8 }}>
                        <Button
                          size="small"
                          icon={<ShoppingCartOutlined />}
                          onClick={(e) => handleAddToCart(item, e)}
                          style={{
                            borderRadius: 8,
                            background: '#52b788',
                            borderColor: '#52b788',
                            color: '#fff',
                          }}
                        />
                        <Button
                          size="small"
                          icon={<HeartOutlined />}
                          onClick={(e) => handleSave(item, e)}
                          style={{ borderRadius: 8, color: '#e5484d', borderColor: '#e5484d' }}
                        />
                      </Space>
                    </Col>
                  </Row>
                </Card>
              )}
            </Col>
          ))}
        </Row>
      )}

      {/* Pagination */}
      {filtered.length > pageSize && (
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={filtered.length}
            onChange={setCurrentPage}
            showSizeChanger={false}
            style={{ display: 'inline-block' }}
            itemRender={(page, type, originalElement) => {
              if (type === 'prev')
                return <Button icon={<LeftOutlined />} style={{ borderRadius: 8 }} />;
              if (type === 'next')
                return <Button icon={<RightOutlined />} style={{ borderRadius: 8 }} />;
              return originalElement;
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CustomerProducts;
